import { useKeycloak } from "@react-keycloak/web";
import { useMemo } from "react";
import { useApi } from "../api/APIContext";
import { AdminUserRole } from "../api/dto";
import type { CompanyPermission } from "../api/dto/MyCompanyPermissions";

/**
 * The frontend counterpart to the gateway's policies. `isAdmin` mirrors a policy's admin shortcut;
 * `hasCompanyPermission` reads the permissions the gateway's `CompanyPolicy` resolved for the caller on that
 * company (the `permissions` field of `GET /v2/companies`), so the rules aren't rebuilt here from raw flags.
 *
 * Resource-specific rules (e.g. "a company-less resource is admin-only, a company-scoped one needs
 * permission X for that company") don't belong here — they belong in a small `<resource>Policy.ts` module
 * built on top of this, one per gated resource, mirroring the matching backend policy. See
 * `checklistPolicy.ts` for the pattern.
 */
export function usePermissions() {
  const { keycloak } = useKeycloak();
  const { myCompanyPermissions, loading } = useApi();

  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const isAdmin = roles.includes(AdminUserRole);

  // `myCompanyPermissions` deliberately does NOT special-case admins (see api.ts's getMyCompanyPermissions) —
  // admin bypass is always `isAdmin`, checked from the realm role, never inferred from this list.
  const hasCompanyPermission = (permission: CompanyPermission, companyId: string): boolean => {
    if (isAdmin) return true;
    return myCompanyPermissions.some(
      (company) => company.companyId === companyId && company.has(permission),
    );
  };

  return { isAdmin, loading, companyPermissions: myCompanyPermissions, hasCompanyPermission };
}
